import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { firebaseHelper } from "ui";
import { PrimaryButton } from "../studioxr/Button.tsx";
import { AuthContext } from "../auth/Auth";
import { useImpersonation } from "../auth/ImpersonationContext";
import { trackEvent, trackEventGA } from "../libs/analyticsManager.ts";
import { sendExceptionToSentry } from "../libs/sentry";
import { sanitizeComment } from "./CancellationSurvey.tsx";

export const PAYMENT_METHOD_REQUESTS_COLLECTION = "paymentMethodRequests";

export const PAYMENT_METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: "paypal", label: "PayPal" },
  { value: "wallet", label: "Apple Pay or Google Pay" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "other", label: "Something else" },
];

const MAX_OTHER_LENGTH = 200;
// A write the server never acknowledges (offline, blocked) must not hang the panel.
export const SAVE_TIMEOUT_MS = 8000;

// Saves still in flight on this page. A remount (a declined card re-keys <Elements>)
// sees them and re-renders when they settle; a reload, which drops them, starts clean.
const inFlight = new Set<string>();
const inFlightListeners = new Set<() => void>();
const setInFlight = (key: string, on: boolean) => {
  if (on) inFlight.add(key);
  else inFlight.delete(key);
  inFlightListeners.forEach((listener) => listener());
};
const subscribeInFlight = (listener: () => void) => {
  inFlightListeners.add(listener);
  return () => {
    inFlightListeners.delete(listener);
  };
};

// Per-tab, per-user memory of confirmed saves, so a remount or reload doesn't ask again.
const sessionValue = {
  get: (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage blocked: a remount may count twice, nothing worse.
    }
  },
};

type Outcome = { uid: string | null; email: boolean };

interface PaymentMethodRequestProps {
  // Not `surface`: that analytics property is reserved for SketchUp panel traffic.
  checkout: "pro" | "team";
  currency?: string | null;
  interval?: string | null;
}

/**
 * "Don't see your payment method?" link under the card form. Counts demand for
 * methods we don't take (GA4 rate vs begin_checkout) and keeps an opt-in list
 * to email if we add one. Never blocks checkout; a failed write only reaches Sentry.
 */
export const PaymentMethodRequest = ({
  checkout,
  currency,
  interval,
}: PaymentMethodRequestProps) => {
  // The auth user, not the profile store: it's what the rules check, and it's
  // set even while the profile is still loading.
  const { currentUser } = useContext(AuthContext) as { currentUser: any };
  const { isImpersonating } = useImpersonation();
  const uid: string | null = currentUser?.uid ?? null;
  // Only a verified address can join the opt-in list (the rules enforce it too).
  const email: string | null = currentUser?.emailVerified
    ? (currentUser.email ?? null)
    : null;
  const sentKey = `paymentMethodRequest:sent:${checkout}:${uid}`;
  const openedKey = `paymentMethodRequest:opened:${checkout}:${uid}`;
  const flightKey = `${checkout}:${uid}`;
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState("");
  // Plain local draft — never persisted or pushed to a store on change.
  const [other, setOther] = useState("");
  const [notifyMe, setNotifyMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // A send finished in this mount, for whoever was signed in at the time.
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  // The draft belongs to whoever is signed in: another account starts from scratch.
  const [draftUid, setDraftUid] = useState(uid);
  if (draftUid !== uid) {
    setDraftUid(uid);
    setOpen(false);
    setMethod("");
    setOther("");
    setNotifyMe(false);
  }
  const rootRef = useRef<HTMLDivElement>(null);
  const thanksRef = useRef<HTMLSpanElement>(null);
  const inFlightHere = useSyncExternalStore(subscribeInFlight, () =>
    inFlight.has(flightKey),
  );
  const otherText =
    method === "other" ? sanitizeComment(other).slice(0, MAX_OTHER_LENGTH) : "";
  const canSend = !!method && (method !== "other" || otherText.length > 0);
  const wantsEmail = notifyMe && !!email;
  const mine = outcome?.uid === uid ? outcome : null;
  const stored = sessionValue.get(sentKey); // "email" | "plain": a confirmed save
  // A save still in flight from a mount a declined card replaced: don't file it twice.
  const pending = !submitting && inFlightHere;
  const submitted = !!mine || stored !== null || pending;
  const promisesEmail = mine ? mine.email : stored === "email";

  // After a send, focus the thank-you so keyboard and screen-reader users hear it,
  // unless they have already moved on (e.g. into the card field) while it saved.
  useEffect(() => {
    if (!mine) return;
    const active = document.activeElement;
    if (
      !active ||
      active === document.body ||
      rootRef.current?.contains(active)
    ) {
      thanksRef.current?.focus();
    }
  }, [mine]);

  // Staff impersonating a user must not file a request, or count one, as them.
  if (isImpersonating) return null;

  const toggle = () => {
    if (!open && !sessionValue.get(openedKey)) {
      sessionValue.set(openedKey, "1");
      trackEventGA("payment_method_request_open", { checkout });
    }
    setOpen(!open);
  };

  const send = async () => {
    if (!canSend || submitting || submitted) return;
    setSubmitting(true);
    const context = {
      method,
      checkout,
      currency: currency ?? null,
      interval: interval ?? null,
    };

    try {
      trackEventGA("payment_method_request", {
        ...context,
        notify_me: wantsEmail,
      });
      trackEvent("Checkout - Payment Method Requested", {
        ...context,
        notifyMe: wantsEmail,
      });
    } catch (error) {
      console.error("trackEvent Payment Method Requested", error);
    }

    const report = (error: unknown, failure: "rejected" | "timeout") =>
      sendExceptionToSentry({
        error,
        contextName: "paymentMethodRequest",
        context: { uid, method },
        tags: { payment_method_request: failure },
      });
    let result: Outcome = { uid, email: false };
    try {
      if (uid) {
        setInFlight(flightKey, true);
        const write = addDoc(
          collection(firebaseHelper.db, PAYMENT_METHOD_REQUESTS_COLLECTION),
          {
            uid,
            email: wantsEmail ? email : null,
            notifyMe: wantsEmail,
            other: otherText,
            ...context,
            createdAt: serverTimestamp(),
          },
        ).then(() => {
          // Only a confirmed save is remembered, so a failed one can be retried later.
          sessionValue.set(sentKey, wantsEmail ? "email" : "plain");
          setInFlight(flightKey, false);
          return "saved" as const;
        });
        // A rejection, even one after the timeout, frees the retry and reaches Sentry.
        write.catch((error) => {
          setInFlight(flightKey, false);
          report(error, "rejected");
        });
        const timeout = new Promise<"timeout">((resolve) =>
          setTimeout(() => resolve("timeout"), SAVE_TIMEOUT_MS),
        );
        const settled = await Promise.race([write, timeout]).catch(
          () => "failed" as const,
        );
        if (settled === "timeout") {
          report(new Error("Payment method request save timed out"), "timeout");
        }
        result = { uid, email: settled === "saved" && wantsEmail };
      }
    } catch (error) {
      setInFlight(flightKey, false);
      report(error, "rejected");
    } finally {
      setSubmitting(false);
      setOutcome(result);
    }
  };

  return (
    <Box ref={rootRef}>
      <Link
        component="button"
        type="button"
        onClick={toggle}
        aria-expanded={open}
        underline="hover"
        // 16px text + 28px padding = a 44px touch target.
        sx={{ fontSize: "13px", color: "text.secondary", py: "14px" }}
      >
        Don't see your payment method?
      </Link>

      <Collapse in={open} unmountOnExit>
        {submitted ? (
          <Typography
            variant="body2"
            role="status"
            tabIndex={-1}
            ref={thanksRef}
            sx={{ "&:focus": { outline: "none" } }}
          >
            Thanks for letting us know! We'll do our best to add this payment
            option
            {promisesEmail ? ", and we'll email you if we do." : "."}
          </Typography>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              How would you like to pay?
            </Typography>
            <RadioGroup
              aria-label="How would you like to pay?"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              {PAYMENT_METHOD_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={option.label}
                  componentsProps={{ typography: { sx: { fontSize: "14px" } } }}
                  sx={{ marginY: "-4px" }}
                />
              ))}
            </RadioGroup>

            {method === "other" && (
              <TextField
                fullWidth
                size="small"
                label="Which one?"
                value={other}
                onChange={(e) => setOther(e.target.value)}
                onKeyDown={(e) => {
                  // The Enter that confirms an IME candidate isn't a send.
                  if (
                    e.key !== "Enter" ||
                    e.nativeEvent.isComposing ||
                    e.keyCode === 229
                  )
                    return;
                  e.preventDefault();
                  send();
                }}
                inputProps={{ maxLength: MAX_OTHER_LENGTH }}
                sx={{ mt: 1 }}
              />
            )}

            {email && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={notifyMe}
                    onChange={(e) => setNotifyMe(e.target.checked)}
                  />
                }
                label={`Email me at ${email} if you add it`}
                // A long address with no hyphen would otherwise run off a phone screen.
                componentsProps={{
                  typography: {
                    sx: { fontSize: "14px", overflowWrap: "anywhere" },
                  },
                }}
                sx={{ mt: 0.5 }}
              />
            )}

            <PrimaryButton
              type="button"
              variant="outlined"
              onClick={send}
              disabled={!canSend || submitting}
              sx={{
                display: "block",
                mt: 1,
                padding: "4px 16px",
                fontWeight: 400,
                fontSize: "14px",
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "#000",
                color: "#000",
                "&:hover": { borderColor: "#000", background: "#f2f2f2" },
              }}
            >
              {submitting ? "Sending..." : "Send"}
            </PrimaryButton>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};
